// The guided setup of a project (PROJECTS-1): its steps, what each needs, and Hebrew explanations
// of refusals. Only the first two steps are required to activate a project; the others are
// warnings until done.
import { PROJECT_ADMIN_MESSAGES, type ProjectSetup } from '@sivan/shared';
import { ApiError, NetworkError } from '../api/client';

export type SetupStepId = 'details' | 'structures' | 'team' | 'readiness';

export interface SetupStep {
  readonly id: SetupStepId;
  readonly label: string;
  readonly done: boolean;
  /** Needed before the project can be activated. */
  readonly required: boolean;
  /** What is missing, when not done. */
  readonly missing: string;
  /** Needed before the first handover can be performed and closed. */
  readonly forHandover: boolean;
}

/** One line of "בדיקת מוכנות למסירות": what a first handover needs, in plain words. */
export interface ReadinessCheck {
  readonly id:
    'structures' | 'apartments' | 'team' | 'checklist' | 'contractors' | 'wording' | 'general';
  readonly label: string;
  readonly done: boolean;
  /** Recommended, not required. */
  readonly optional: boolean;
  readonly hint: string;
}

export function readinessChecks(setup: ProjectSetup): ReadinessCheck[] {
  return [
    {
      id: 'structures',
      label: 'בניינים וקומות',
      done: setup.buildings > 0 && setup.floors > 0,
      optional: false,
      hint: setup.buildings === 0 ? 'אין עדיין בניינים.' : 'לבניינים אין קומות.',
    },
    {
      id: 'apartments',
      label: 'דירות',
      done: setup.apartments > 0,
      optional: false,
      hint: 'מוסיפים דירות בגיליון הדירות (הקלדה או הדבקה מ-Excel).',
    },
    {
      id: 'team',
      label: 'עובדים עם הרשאה בפרויקט',
      done: setup.membersWithRoles > 0,
      optional: false,
      hint:
        setup.members === 0
          ? 'אין עובדים בצוות הפרויקט.'
          : 'לעובדים בצוות אין עדיין הרשאות בפרויקט.',
    },
    {
      id: 'checklist',
      label: 'רשימת הבדיקה',
      done: setup.templateItems > 0,
      optional: false,
      // Automatic: publishing the company checklist applies it to every project (DR-195).
      hint:
        setup.masterLatest === null
          ? 'נקבעת אוטומטית מרשימת הבדיקה של סיון, אחרי שמנהל־על מפרסם אותה פעם אחת ("ניהול פרויקטים" ← "רשימת הבדיקה של סיון").'
          : 'רשימת הבדיקה של סיון לא הוחלה על הפרויקט הזה.',
    },
    {
      id: 'contractors',
      label: 'קבלנים מבצעים',
      done: setup.contractors > 0 && setup.workCategories > 0,
      optional: false,
      hint: 'ליקוי מנותב לקבלן לפי תחום העבודה.',
    },
    {
      id: 'wording',
      label: 'נוסח ההצהרה של החברה',
      done: setup.acknowledgment,
      optional: false,
      hint: 'בלעדיו אי אפשר לסגור מסירה. מוזן על ידי מנהל־על, מהנוסח המאושר.',
    },
    {
      id: 'general',
      label: 'בדיקות כלליות (מפתחות, שלטים, חניה)',
      done: setup.generalChecks > 0,
      optional: true,
      hint: 'מומלץ: מה שנמסר לדייר בנוסף לבדיקת החדרים.',
    },
  ];
}

export function setupSteps(setup: ProjectSetup): SetupStep[] {
  const checks = readinessChecks(setup);
  const ready = checks.every((c) => c.done || c.optional);
  return [
    {
      id: 'details',
      label: 'הגדרת פרטי הפרויקט',
      done: true,
      required: true,
      missing: '',
      forHandover: false,
    },
    {
      id: 'structures',
      label: 'הוספת בניינים ודירות',
      done: setup.buildings > 0 && setup.floors > 0 && setup.apartments > 0,
      // Activating needs buildings and floors; apartments can follow.
      required: !(setup.buildings > 0 && setup.floors > 0),
      missing:
        setup.buildings === 0
          ? 'אין עדיין בניינים.'
          : setup.floors === 0
            ? 'לבניינים אין קומות.'
            : 'לא נוספו דירות.',
      forHandover: true,
    },
    {
      id: 'team',
      label: 'שיוך עובדים',
      done: setup.membersWithRoles > 0,
      required: false,
      missing:
        setup.members === 0
          ? 'אין עובדים בצוות הפרויקט.'
          : 'לעובדים בצוות אין עדיין הרשאות בפרויקט.',
      forHandover: true,
    },
    {
      id: 'readiness',
      label: 'בדיקת מוכנות למסירות',
      done: ready,
      required: false,
      missing: ready
        ? ''
        : `חסרים: ${checks
            .filter((c) => !c.done && !c.optional)
            .map((c) => c.label)
            .join(', ')}.`,
      forHandover: true,
    },
  ];
}

/** A Hebrew explanation of a failed project, building or floor change. */
export function explainProjectError(error: unknown): string {
  if (error instanceof ApiError) {
    const fields = Object.values((error.body['fields'] ?? {}) as Record<string, string>);
    const conflict = (error.body['conflict'] as { code?: string } | undefined)?.code;
    const known = [...fields, conflict ?? ''].map((c) => PROJECT_ADMIN_MESSAGES[c]).find(Boolean);
    if (known !== undefined) return known;
    if (error.status === 403) return 'אין לך הרשאה לפעולה זו.';
    if (error.status === 404) return 'הפרויקט לא נמצא או שאין לך גישה אליו.';
    if (error.status === 409) return PROJECT_ADMIN_MESSAGES['stale'] ?? '';
    if (error.status === 422) return 'חלק מהערכים אינם תקינים. בדקו ונסו שוב.';
  }
  if (error instanceof NetworkError) return 'אין חיבור לשרת. השינוי לא נשמר; נסו שוב.';
  return 'השמירה נכשלה. נסו שוב.';
}
